import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useConfig } from "../hooks/useConfig";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { fmtDate, istToday } from "@/lib/dateFormat";

export default function SSRecordDeliveryPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getValues } = useConfig();
  const ssId = searchParams.get("ss_id") || "";
  const ssName = searchParams.get("ss_name") || "";

  const PLATFORMS = getValues("ss_platforms");
  const [platform, setPlatform] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [poSuggestions, setPoSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(istToday());
  const [appointmentDt, setAppointmentDt] = useState("");
  const [grnNumber, setGrnNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [stockData, setStockData] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!ssId) return;
    api.get(`/ss-inventory/stock/${ssId}`).then(r => {
      const data = r.data || [];
      setStockData(data);
      setLineItems(data.map(s => ({
        sku_id: s.sku_id, sku_name: s.sku_name, quantity_po: 0,
        available_batches: (s.batches || []).filter(b => b.quantity > 0).map(b => ({
          batch_number: b.batch_number || "default",
          available_qty: b.quantity || 0,
          checked: (s.batches || []).filter(x => x.quantity > 0).length === 1,
          qty_accepted: 0,
        })),
        manual_batch: "", manual_qty: 0,
      })));
    }).catch(() => {});
  }, [ssId]);

  useEffect(() => {
    if (!ssId || !platform) { setPoSuggestions([]); return; }
    api.get("/ss-inventory/po-suggestions", { params: { ss_id: ssId, platform } }).then(r => setPoSuggestions(r.data || [])).catch(() => {});
  }, [ssId, platform]);

  const filteredSuggestions = poSuggestions.filter(s => !poNumber || s.po_number.toLowerCase().includes(poNumber.toLowerCase()));

  const updateBatch = (skuIdx, batchIdx, field, val) => {
    setLineItems(prev => {
      const items = [...prev];
      const batches = [...items[skuIdx].available_batches];
      batches[batchIdx] = { ...batches[batchIdx], [field]: val };
      if (field === "checked" && !val) batches[batchIdx].qty_accepted = 0;
      items[skuIdx] = { ...items[skuIdx], available_batches: batches };
      return items;
    });
  };

  const updateSkuField = (i, field, val) => {
    setLineItems(prev => { const items = [...prev]; items[i] = { ...items[i], [field]: val }; return items; });
  };

  const getSkuTotalAccepted = (item) => {
    if (item.available_batches.length > 0)
      return item.available_batches.filter(b => b.checked).reduce((s, b) => s + (parseInt(b.qty_accepted) || 0), 0);
    return parseInt(item.manual_qty) || 0;
  };

  const handleSubmit = async () => {
    if (!platform) { toast.error("Select a platform"); return; }
    if (!grnNumber.trim()) { toast.error("GRN Number is mandatory"); return; }
    const items = [];
    for (const li of lineItems) {
      const totalAccepted = getSkuTotalAccepted(li);
      if (totalAccepted <= 0) continue;
      if (li.available_batches.length > 0) {
        for (const b of li.available_batches) {
          if (b.checked && parseInt(b.qty_accepted) > 0) {
            items.push({ sku_id: li.sku_id, sku_name: li.sku_name, batch_number: b.batch_number, quantity_po: parseInt(li.quantity_po) || 0, quantity_accepted: parseInt(b.qty_accepted), variance: (parseInt(li.quantity_po) || 0) - totalAccepted });
          }
        }
      } else if (parseInt(li.manual_qty) > 0) {
        items.push({ sku_id: li.sku_id, sku_name: li.sku_name, batch_number: li.manual_batch.toUpperCase().replace(/[^A-Z0-9]/g, ""), quantity_po: parseInt(li.quantity_po) || 0, quantity_accepted: parseInt(li.manual_qty), variance: (parseInt(li.quantity_po) || 0) - parseInt(li.manual_qty) });
      }
    }
    if (!items.length) { toast.error("Enter accepted qty for at least one SKU"); return; }
    setSubmitting(true);
    try {
      await api.post("/ss-inventory/record-delivery", {
        ss_customer_id: ssId, ss_name: ssName, platform, platform_po_number: poNumber,
        delivery_date: deliveryDate, appointment_datetime: appointmentDt,
        platform_grn_number: grnNumber, notes,
        line_items: items
      });
      toast.success("Delivery recorded");
      navigate("/ss-inventory");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ss-inventory")} className="text-zinc-400 hover:text-zinc-600"><ArrowLeft size={18} /></button>
        <div><h1 className="font-heading text-xl font-bold text-zinc-900">Record Platform Delivery</h1><p className="text-sm text-zinc-500">{ssName}</p></div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Platform *</Label>
            <select value={platform} onChange={e => setPlatform(e.target.value)} className="mt-1 w-full h-8 text-xs border border-zinc-200 rounded-sm px-2">
              <option value="">Select Platform</option>
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="relative">
            <Label className="text-xs">PO Number</Label>
            <Input value={poNumber} onChange={e => { setPoNumber(e.target.value); setShowSuggestions(true); }} onFocus={() => setShowSuggestions(true)} placeholder="Type or select PO number" className="h-8 text-xs mt-1" />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-48 overflow-y-auto">
                {filteredSuggestions.map((s, i) => (
                  <div key={i} onClick={() => { setPoNumber(s.po_number); setShowSuggestions(false); }} className="px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-xs flex items-center justify-between">
                    <span>{s.po_number} — {s.facility_name || "—"} — {fmtDate(s.po_date) || "—"}</span>
                    {s.already_delivered && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded-sm flex items-center gap-0.5"><AlertTriangle size={9} /> Already recorded</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div><Label className="text-xs">Delivery Date *</Label><Input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="h-8 text-xs mt-1" /></div>
          <div><Label className="text-xs">Appointment Date & Time</Label><Input type="datetime-local" value={appointmentDt} onChange={e => setAppointmentDt(e.target.value)} className="h-8 text-xs mt-1" /></div>
          <div><Label className="text-xs">Platform GRN Number *</Label><Input value={grnNumber} onChange={e => setGrnNumber(e.target.value)} placeholder="e.g. GRN-2026-001" className="h-8 text-xs mt-1" /></div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <h3 className="text-sm font-semibold text-zinc-800 mb-3">SKU Delivery Details</h3>
        {lineItems.map((li, skuIdx) => {
          const totalAccepted = getSkuTotalAccepted(li);
          const variance = (parseInt(li.quantity_po) || 0) - totalAccepted;
          return (
            <div key={skuIdx} className="mb-4 pb-4 border-b border-zinc-100 last:border-0">
              <div className="flex items-center justify-between mb-2">
                <div><span className="text-sm font-medium text-zinc-800">{li.sku_name}</span><span className="text-[10px] text-zinc-400 ml-2">{li.sku_id}</span></div>
                <div className="flex items-center gap-3">
                  <div><label className="text-[10px] text-zinc-500">PO Qty</label><Input type="number" value={li.quantity_po || ""} onChange={e => updateSkuField(skuIdx, "quantity_po", e.target.value)} className="h-7 text-xs w-20 text-right" /></div>
                  <div className="text-[10px] text-zinc-500">Accepted: <strong>{totalAccepted}</strong></div>
                  <div className={`text-[10px] font-mono ${variance > 0 ? "text-red-600" : variance < 0 ? "text-green-600" : "text-zinc-400"}`}>Var: {variance}</div>
                </div>
              </div>
              {li.available_batches.length > 0 ? (
                <div className="ml-4 space-y-1">
                  {li.available_batches.map((b, bIdx) => (
                    <div key={bIdx} className="flex items-center gap-2">
                      <input type="checkbox" checked={b.checked || false} onChange={e => updateBatch(skuIdx, bIdx, "checked", e.target.checked)} className="rounded" />
                      <span className="text-xs font-mono w-28">{b.batch_number}</span>
                      <span className="text-[10px] text-zinc-400 w-20">Avail: {b.available_qty}</span>
                      <Input type="number" disabled={!b.checked} value={b.qty_accepted || ""} onChange={e => updateBatch(skuIdx, bIdx, "qty_accepted", parseInt(e.target.value) || 0)} placeholder="Accepted" className="h-7 text-xs w-24" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ml-4 flex items-center gap-2">
                  <Input value={li.manual_batch || ""} onChange={e => updateSkuField(skuIdx, "manual_batch", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} placeholder="Batch #" className="h-7 text-xs w-32" />
                  <Input type="number" value={li.manual_qty || ""} onChange={e => updateSkuField(skuIdx, "manual_qty", parseInt(e.target.value) || 0)} placeholder="Qty" className="h-7 text-xs w-24" />
                  <span className="text-[10px] text-zinc-400">(no existing batches — manual entry)</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <Label className="text-xs">Notes</Label>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." className="text-xs mt-1 h-16" />
      </div>

      <div className="flex gap-3">
        <Button onClick={handleSubmit} disabled={submitting} className="rounded-sm text-xs">{submitting ? "Recording..." : "Record Delivery"}</Button>
        <Button variant="outline" onClick={() => navigate("/ss-inventory")} className="rounded-sm text-xs">Cancel</Button>
      </div>
    </div>
  );
}
